import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, X, Plus, Loader2, AlertCircle, Search, Check, Lock } from 'lucide-react';
import axiosInstance from '../api/axios';
import { resolve403ErrorMessage } from '../request/getApiErrorMessage.ts';
import { MODULE_KEYS, hasModuleAccess } from '../constants/moduleAccess.ts';
import { useOrgModuleAccess } from '../hooks/useOrgModuleAccess.ts';

/**
 * Multi-select dropdown for the per-node "system" field on a flowchart node.
 *
 * Wire format (intentional): value/onChange use a comma-separated string so the
 * component is a drop-in replacement for the old `<input type="text">` that
 * editForm.system already lives in. The backend splits this exact format on
 * '/' or ',' to populate the flowchart_node_systems junction table, so no
 * upstream contract changes are needed.
 *
 *   value      = "SFDC, CPQ"
 *   onChange   = (next: string) => void   // e.g. "SFDC, CPQ, ERP"
 *
 * Picking an "+ Add new system" option pops a modal that takes a display name
 * and a system code (system_id) and POSTs /api/systems. The new system is
 * appended to both the dropdown options and the current selection on success.
 */
function SystemMultiSelect({ value, onChange, error, onSystemCreated }) {
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const [showCreate, setShowCreate] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createCode, setCreateCode] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [showAccessDenied, setShowAccessDenied] = useState(false);

  const { data: moduleAccess, isSuccess: moduleAccessReady } = useOrgModuleAccess();
  const canCreateSystem = useMemo(
    () => hasModuleAccess(moduleAccess, MODULE_KEYS.SYSTEMS),
    [moduleAccess]
  );

  const rootRef = useRef(null);

  // Selected system names parsed from the comma/slash-delimited string.
  const selectedNames = useMemo(() => {
    if (!value) return [];
    return value
      .split(/[,/]/)
      .map((s) => s.trim())
      .filter(Boolean);
  }, [value]);

  const isSelected = (name) =>
    selectedNames.some((n) => n.toLowerCase() === (name || '').toLowerCase());

  // Fetch the catalog once on mount. Large page so we get everything; the
  // listing endpoint is paginated as of #138.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const res = await axiosInstance.get('/api/systems', { params: { page_size: 200 } });
        if (cancelled) return;
        setSystems(Array.isArray(res.data?.systems) ? res.data.systems : []);
      } catch (err) {
        if (!cancelled) setLoadError(err?.response?.data?.error || 'Failed to load systems');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filteredSystems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return systems;
    return systems.filter((s) => {
      const name = (s.name || '').toLowerCase();
      const code = (s.systemId || '').toLowerCase();
      const owner = (s.systemOwner || '').toLowerCase();
      return name.includes(q) || code.includes(q) || owner.includes(q);
    });
  }, [systems, query]);

  const emit = (names) => onChange(names.join(', '));

  const toggleByName = (name) => {
    if (!name) return;
    const cleaned = name.trim();
    if (!cleaned) return;
    const idx = selectedNames.findIndex((n) => n.toLowerCase() === cleaned.toLowerCase());
    if (idx >= 0) {
      emit(selectedNames.filter((_, i) => i !== idx));
    } else {
      emit([...selectedNames, cleaned]);
    }
  };

  const removeByName = (name) => {
    emit(selectedNames.filter((n) => n.toLowerCase() !== name.toLowerCase()));
  };

  const openCreateModal = () => {
    if (!canCreateSystem) {
      setShowAccessDenied(true);
      setOpen(false);
      return;
    }
    setCreateName(query.trim());
    setCreateCode(query.trim().toUpperCase().replace(/\s+/g, '_'));
    setCreateError(null);
    setShowCreate(true);
    setOpen(false);
  };

  const closeCreateModal = () => {
    if (creating) return;
    setShowCreate(false);
    setCreateName('');
    setCreateCode('');
    setCreateError(null);
  };

  const submitCreate = async () => {
    if (!canCreateSystem) {
      setShowCreate(false);
      setShowAccessDenied(true);
      return;
    }
    const name = createName.trim();
    const code = createCode.trim();
    if (!name || !code) {
      setCreateError('Both Name and Code are required.');
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const res = await axiosInstance.post('/api/systems', {
        systemId: code,
        name,
        systemOwner: '',
        keySystem: false,
        socReport: false,
        categorization: [],
        rationale: '',
        ipe: [],
      });
      const created = res.data || { name, systemId: code };
      const createdName = (created.name || name).trim();
      const createdId = typeof created.id === 'string' ? created.id.trim() : '';
      if (onSystemCreated && createdId && createdName) {
        onSystemCreated({ id: createdId, name: createdName });
      }
      // Prepend so the new system is visible at the top of the dropdown next open.
      setSystems((prev) => [created, ...prev]);
      // Add to selection unless it's already there (shouldn't be, but defensive).
      if (!isSelected(created.name || name)) {
        emit([...selectedNames, created.name || name]);
      }
      setShowCreate(false);
      setCreateName('');
      setCreateCode('');
    } catch (err) {
      setCreateError(resolve403ErrorMessage(err, err?.response?.data?.error || 'Failed to create system.'));
    } finally {
      setCreating(false);
    }
  };

  // Helpful "Add new" hint when the search query has no exact match.
  const exactMatch =
    query.trim().length > 0 &&
    systems.some((s) => (s.name || '').toLowerCase() === query.trim().toLowerCase());
  const showAddNewHint = query.trim().length > 0 && !exactMatch;

  return (
    <div ref={rootRef} className="relative">
      {/* Selected chips + dropdown toggle */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex flex-wrap items-center gap-1.5 min-h-[42px] px-3 py-1.5 border rounded-lg bg-white text-left focus:outline-none focus:ring-2 focus:ring-blue-500 ${
          error ? 'border-red-500' : 'border-gray-300'
        }`}
      >
        {selectedNames.length === 0 ? (
          <span className="text-gray-400 text-sm">Select systems...</span>
        ) : (
          selectedNames.map((name) => (
            <span
              key={name}
              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 rounded"
            >
              {name}
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  removeByName(name);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    removeByName(name);
                  }
                }}
                className="hover:bg-blue-100 rounded p-0.5 inline-flex cursor-pointer"
                aria-label={`Remove ${name}`}
              >
                <X className="h-3 w-3" />
              </span>
            </span>
          ))
        )}
        <ChevronDown className="h-4 w-4 text-gray-400 ml-auto shrink-0" />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-hidden flex flex-col">
          {/* Search */}
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search systems..."
                className="w-full pl-8 pr-2 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Options */}
          <div className="overflow-y-auto flex-1 min-h-0">
            {loading ? (
              <div className="px-3 py-6 text-center text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin mx-auto mb-1" />
                Loading systems…
              </div>
            ) : loadError ? (
              <div className="px-3 py-4 text-sm text-red-600 flex items-center gap-2">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {loadError}
              </div>
            ) : filteredSystems.length === 0 ? (
              <div className="px-3 py-4 text-sm text-gray-500">
                {query ? `No systems match "${query}".` : 'No systems in the catalog yet.'}
              </div>
            ) : (
              filteredSystems.map((s) => {
                const checked = isSelected(s.name);
                return (
                  <button
                    key={s.id || s.systemId || s.name}
                    type="button"
                    onClick={() => toggleByName(s.name)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 text-left"
                  >
                    <span
                      className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 ${
                        checked ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-300'
                      }`}
                      aria-hidden="true"
                    >
                      {checked && <Check className="h-3 w-3" />}
                    </span>
                    <span className="flex-1 min-w-0 truncate">{s.name}</span>
                    {s.systemId && (
                      <span className="text-xs text-gray-400 font-mono shrink-0">{s.systemId}</span>
                    )}
                  </button>
                );
              })
            )}
          </div>

          {/* Add new — requires Systems module access (shown after module-access API loads) */}
          {moduleAccessReady && (
          <div className="border-t border-gray-100 p-1 bg-gray-50">
            <button
              type="button"
              onClick={openCreateModal}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded ${
                canCreateSystem
                  ? 'text-blue-700 hover:bg-blue-50'
                  : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              {canCreateSystem ? (
                <Plus className="h-4 w-4" />
              ) : (
                <Lock className="h-4 w-4 shrink-0" />
              )}
              {canCreateSystem ? (
                showAddNewHint ? (
                  <>Add new system <strong>“{query.trim()}”</strong></>
                ) : (
                  'Add new system'
                )
              ) : (
                'Add new system (Systems access required)'
              )}
            </button>
          </div>
          )}
        </div>
      )}

      {/* Module access denied — creating systems requires Systems module */}
      {showAccessDenied && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/45 flex items-center justify-center p-4"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowAccessDenied(false);
          }}
        >
          <div
            className="bg-white rounded-xl shadow-2xl border border-red-200/80 max-w-md w-full overflow-hidden"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="systems-access-denied-title"
          >
            <div className="px-6 pb-6 pt-8 text-center">
              <div className="relative mx-auto mb-5 flex h-14 w-14 items-center justify-center">
                <span className="absolute inset-0 rounded-2xl bg-red-100/70" aria-hidden="true" />
                <Lock className="relative h-7 w-7 text-red-600" strokeWidth={1.75} aria-hidden="true" />
              </div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-red-600">Access restricted</p>
              <h2 id="systems-access-denied-title" className="mt-2 text-lg font-bold text-gray-900">
                No Systems module access
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-gray-600">
                Creating a new system requires the Systems module. You can still select existing systems
                for this node. Contact your organization administrator if you need access.
              </p>
              <button
                type="button"
                onClick={() => setShowAccessDenied(false)}
                className="mt-6 inline-flex items-center justify-center rounded-lg border border-red-200 bg-white px-4 py-2.5 text-sm font-medium text-red-700 shadow-sm hover:border-red-300 hover:bg-red-50"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create modal */}
      {showCreate && canCreateSystem && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/45 flex items-center justify-center p-4"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeCreateModal();
          }}
        >
          <div
            className="bg-white rounded-xl shadow-2xl border border-gray-200 max-w-md w-full"
            role="dialog"
            aria-modal="true"
            aria-labelledby="systems-create-modal-title"
          >
            <div className="px-5 py-4 border-b border-gray-200">
              <h2 id="systems-create-modal-title" className="text-base font-semibold text-gray-900">
                Add new system
              </h2>
              <p className="text-xs text-gray-500 mt-1">
                Adds a row to the canonical systems catalog and selects it for this node.
              </p>
            </div>
            <div className="px-5 py-4 space-y-3">
              <div>
                <label htmlFor="sysmsel-name" className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-600">*</span>
                </label>
                <input
                  id="sysmsel-name"
                  type="text"
                  value={createName}
                  onChange={(e) => {
                    setCreateName(e.target.value);
                    setCreateError(null);
                  }}
                  placeholder="e.g. SAP ECC (FI/CO)"
                  className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label htmlFor="sysmsel-code" className="block text-sm font-medium text-gray-700 mb-1">
                  Code <span className="text-red-600">*</span>
                </label>
                <input
                  id="sysmsel-code"
                  type="text"
                  value={createCode}
                  onChange={(e) => {
                    setCreateCode(e.target.value);
                    setCreateError(null);
                  }}
                  placeholder="Short code (unique in your org)"
                  className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                />
              </div>
              {createError && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  {createError}
                </div>
              )}
            </div>
            <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex justify-end gap-2 rounded-b-xl">
              <button
                type="button"
                onClick={closeCreateModal}
                disabled={creating}
                className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitCreate}
                disabled={creating || !createName.trim() || !createCode.trim()}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed"
              >
                {creating && <Loader2 className="h-4 w-4 animate-spin" />}
                {creating ? 'Creating…' : 'Create system'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SystemMultiSelect;
